/**
 * App chrome: a 240px left sidebar plus a 56px top strip — the same shell as
 * fh-hris, so the two internal apps read as one product.
 *
 * The sidebar is grouped by TOOL rather than by page, because that is how this
 * app grows: a new tool is a new group, and nothing above it changes.
 */

import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FiCpu, FiFileText, FiGrid, FiLogOut, FiMenu, FiSave, FiX } from "react-icons/fi";
import logo from "@/assets/images/fh-horizontal-logo.png";
import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui";

interface NavItem { to: string; label: string; icon: ReactNode; end?: boolean }
interface NavGroup { label: string; items: NavItem[] }

const NAV: NavGroup[] = [
  { label: "Playground", items: [{ to: "/", label: "Tools", icon: <FiGrid />, end: true }] },
  {
    label: "OCR Lab",
    items: [
      { to: "/ocrlab", label: "Compare", icon: <FiCpu />, end: true },
      { to: "/ocrlab/runs", label: "Saved runs", icon: <FiSave /> },
    ],
  },
  {
    label: "PDF Lab",
    items: [
      { to: "/pdflab", label: "Parse", icon: <FiFileText />, end: true },
      { to: "/pdflab/runs", label: "Saved runs", icon: <FiSave /> },
    ],
  },
];

function initialsOf(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
}

export function AppShell({ user }: { user: { name: string; email?: string } }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const signOut = () => {
    const csrf = document.cookie.split("; ").find((c) => c.startsWith("fhp_csrf="))?.split("=")[1] ?? "";
    fetch("/api/auth/logout", { method: "POST", credentials: "include", headers: { "X-CSRF-Token": csrf } })
      .finally(() => { window.location.href = "/login"; });
  };

  const nav = (
    <>
      <div className="flex h-14 items-center gap-2 px-4">
        <img src={logo} alt="Firehorse" className="h-6" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {NAV.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="px-2 pb-1 text-fh-meta font-medium uppercase tracking-wide text-fh-text-label">
              {group.label}
            </p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "mb-0.5 flex items-center gap-2.5 rounded-fh-input px-2.5 py-2 text-fh-body transition",
                    isActive
                      ? "bg-fh-navy font-medium text-white"
                      : "text-fh-text-body hover:bg-fh-surface-avatar",
                  )
                }
              >
                <span className="text-base">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-fh-border-soft p-3">
        <div className="flex items-center gap-2.5 px-1.5 py-1">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fh-surface-avatar text-[11px] font-medium text-fh-text-mid">
            {initialsOf(user.name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-fh-body font-medium text-fh-text-strong">{user.name}</p>
            {user.email && <p className="truncate text-fh-meta text-fh-text-muted">{user.email}</p>}
          </div>
          <IconButton label="Sign out" onClick={signOut}><FiLogOut /></IconButton>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-fh-surface-page">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-fh-border-soft bg-fh-surface-sidebar md:flex">
        {nav}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-60 flex-col bg-fh-surface-sidebar shadow-xl">
            <div className="absolute right-2 top-3">
              <IconButton label="Close menu" onClick={() => setOpen(false)}><FiX /></IconButton>
            </div>
            {nav}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-3 border-b border-fh-border-soft bg-white/95 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <span className="md:hidden">
              <IconButton label="Open menu" onClick={() => setOpen(true)}><FiMenu /></IconButton>
            </span>
            <h1 className="text-fh-body font-medium text-fh-text-strong">
              {location.pathname.startsWith("/ocrlab")
                ? "OCR Lab"
                : location.pathname.startsWith("/pdflab")
                  ? "PDF Lab"
                  : "Playground"}
            </h1>
          </div>
          <span className="text-fh-meta text-fh-text-muted">stage</span>
        </header>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
